/**
 * Theme registry — platform catalog, per-restaurant custom apps, and runtime loaders.
 *
 * Custom apps: src/themes/by-restaurant/<env>/<slug>/index.jsx
 * Environment (staging | production) is resolved from restaurant UUID — see restaurant-theme-env.js
 * and project_docs/docs/RESTAURANT_THEMES.md §5.4.
 *
 * @see project_docs/docs/RESTAURANT_THEMES.md
 */

import { getThemeEnvForRestaurant } from './by-restaurant/restaurant-theme-env';

/** Legacy alias when one custom app existed per restaurant. */
export const CUSTOM_THEME_ID = 'custom';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** @typedef {'staging' | 'production'} ThemeSourceEnv */

/** Lazy-loaded platform (base) themes — available to all restaurants when admin-enabled. */
export const PLATFORM_THEMES = {
  a: () => import('./theme-a'),
  b: () => import('./theme-b'),
  c: () => import('./theme-c'),
};

/** Global catalog metadata (admin panel toggles per restaurant). */
export const THEME_CATALOG = [
  {
    id: 'a',
    name: 'Classic',
    description: "Table Tap's default customer experience.",
    kind: 'platform',
  },
  {
    id: 'b',
    name: 'Minimal',
    description: 'Standalone minimal customer app — light shell, indigo accents.',
    kind: 'platform',
  },
  {
    id: 'c',
    name: 'Classic Inverted',
    description: 'Classic layout with a light top shell and dark menu content area.',
    kind: 'platform',
  },
];

/** @deprecated Use THEME_CATALOG */
export const THEME_OPTIONS = THEME_CATALOG;

/** @deprecated Use PLATFORM_THEMES */
export const THEMES = PLATFORM_THEMES;

/** True when theme_id refers to a custom app (uuid or legacy ''custom''). */
export function isCustomThemeId(themeId) {
  if (!themeId) return false;
  if (themeId === CUSTOM_THEME_ID) return true;
  return UUID_RE.test(themeId);
}

/** True when the restaurants row is using a custom app bundle. */
export function isCustomThemeActive(themeId, themeType) {
  return themeType === 'custom' || isCustomThemeId(themeId);
}

/**
 * Build slug → loader map from a webpack require.context.
 * @param {__WebpackModuleApi.RequireContext} context
 */
function loadEnvThemeModules(context) {
  /** @type {Record<string, () => Promise<unknown>>} */
  const bySlug = {};
  context.keys().forEach((relativeKey) => {
    const segments = relativeKey.replace(/^\.\//, '').split('/');
    if (segments.length === 2 && segments[1] === 'index.jsx') {
      const slug = segments[0];
      bySlug[slug] = () => Promise.resolve(context(relativeKey));
    }
  });
  return bySlug;
}

/** Per-environment custom app bundles keyed by slug. */
const envThemeModules = {
  staging: loadEnvThemeModules(require.context('./by-restaurant/staging', true, /index\.jsx$/)),
  production: loadEnvThemeModules(
    require.context('./by-restaurant/production', true, /index\.jsx$/),
  ),
};

/**
 * Resolve lazy loader for one custom app.
 * @param {string} restaurantId
 * @param {{ slug?: string }} [options]
 */
export function getRestaurantThemeLoader(restaurantId, options = {}) {
  if (!restaurantId) return null;

  const { slug } = options;
  const env = getThemeEnvForRestaurant(restaurantId);
  if (!env || !slug) return null;

  return envThemeModules[env]?.[slug] || null;
}

/** Explicit registrations override glob (optional). */
export const RESTAURANT_THEMES = {};

/**
 * Resolve lazy loader for guest app runtime.
 * @param {string | { restaurantId?: string, themeId?: string, themeType?: string, customThemeSlug?: string }} params
 */
export function getThemeLoader(params) {
  if (typeof params === 'string') {
    return PLATFORM_THEMES[params] || PLATFORM_THEMES.a;
  }

  const { restaurantId, themeId, themeType, customThemeSlug } = params || {};
  const isCustom = isCustomThemeActive(themeId, themeType);

  if (isCustom && restaurantId) {
    const loader =
      RESTAURANT_THEMES[restaurantId] ||
      getRestaurantThemeLoader(restaurantId, { slug: customThemeSlug });
    if (loader) return loader;
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[themes] No custom app for restaurant ${restaurantId}` +
          (customThemeSlug ? ` (slug: ${customThemeSlug})` : '') +
          '; falling back to Classic.',
      );
    }
  }

  if (!isCustom && themeId && PLATFORM_THEMES[themeId]) {
    return PLATFORM_THEMES[themeId];
  }

  return PLATFORM_THEMES.a;
}

/** Display metadata for a platform theme id. */
export function getThemeMeta(themeId, customMeta = null) {
  if (isCustomThemeId(themeId) && customMeta) {
    return {
      id: customMeta.id || themeId,
      name: customMeta.name || 'Custom App',
      description: customMeta.description || 'Bespoke customer experience for this restaurant.',
      kind: 'custom',
    };
  }
  return THEME_CATALOG.find((t) => t.id === themeId) || THEME_CATALOG[0];
}

/** Selection value for ThemeSelector from restaurant row. */
export function getActiveThemeSelection(themeId, themeType) {
  if (isCustomThemeActive(themeId, themeType)) {
    return themeId || CUSTOM_THEME_ID;
  }
  return themeId || 'a';
}

/** Default slug for a new custom app folder. */
export function slugifyCustomThemeName(name) {
  return (
    (name || 'custom-app')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'custom-app'
  );
}
